import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Check, X, RefreshCcw } from 'lucide-react';
import { Logo } from '../components/Logo';

// Single component that renders both /subscription/success and /subscription/cancel.
// Polls the backend for the payment status — webhooks update asynchronously, so the user
// may land here a few seconds before the status flips to "succeeded".
export default function SubscriptionResult({ outcome }) {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const paymentId = params.get('payment_id');
    const [payment, setPayment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [polls, setPolls] = useState(0);

    // Poll up to ~60s (30 × 2s). Webhooks usually land in <5s but a flaky tunnel
    // or processor delay can stretch that. After the budget runs out the user
    // gets a manual "Reîncarcă" button instead of a stale loading state.
    const MAX_POLLS = 30;
    useEffect(() => {
        if (!paymentId) { setLoading(false); return; }
        let mounted = true;
        const poll = async () => {
            try {
                // Actively ask the processor for the real status. This covers the case
                // where the IPN webhook never lands (e.g. sandbox cert mismatch): it
                // confirms paid AND surfaces a declined card promptly. Idempotent.
                try { await api.post(`/r/subscription/payments/${paymentId}/recheck`); } catch { /* fall back to the list read below */ }
                const { data } = await api.get('/r/subscription/payments');
                const p = (data.items || []).find(x => x.id === paymentId);
                if (mounted) {
                    setPayment(p || null);
                    setLoading(false);
                }
                if (p && p.status === 'pending' && polls < MAX_POLLS) {
                    setTimeout(() => setPolls(n => n + 1), 2000);
                }
            } catch {
                if (mounted) setLoading(false);
            }
        };
        poll();
        return () => { mounted = false; };
    }, [paymentId, polls]);

    const pollingExhausted = payment?.status === 'pending' && polls >= MAX_POLLS;

    const isPaid = payment?.status === 'succeeded';
    const isCancel = outcome === 'cancel' || payment?.status === 'cancelled' || payment?.status === 'failed';
    // Distinguish a deliberate user cancel from a declined/failed card — different copy.
    const userCancelled = outcome === 'cancel' && payment?.status !== 'failed';

    return (
        <div className="grain" style={{ minHeight: '100vh', padding: '2rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="panel" style={{ maxWidth: 480, width: '100%', textAlign: 'center', padding: '2rem' }} data-testid="subscription-result">
                <div style={{ marginBottom: '1.4rem' }}>
                    <Logo size="lg" />
                </div>
                {isPaid ? (
                    <>
                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(80,255,180,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                            <Check size={36} color="var(--neon-teal)" />
                        </div>
                        <h1 className="headline" style={{ fontSize: '1.8rem', margin: '0 0 0.5rem' }}>Plata confirmată</h1>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '1.2rem' }}>
                            Abonamentul tău e activ. Factura a fost trimisă pe email.
                        </p>
                        <div style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '0.9rem', marginBottom: '1.2rem', textAlign: 'left' }}>
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-faint)' }}>Sumă</div>
                            <div style={{ fontWeight: 600, fontSize: '1.2rem' }}>{payment.amount_ron.toFixed(2)} RON</div>
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-faint)', marginTop: 8 }}>Activ până la</div>
                            <div style={{ fontWeight: 600 }}>{payment.subscription_expires_at ? new Date(payment.subscription_expires_at).toLocaleDateString('ro-RO') : '—'}</div>
                        </div>
                        <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => nav('/r')} data-testid="result-dashboard">
                            Spre dashboard
                        </button>
                    </>
                ) : isCancel ? (
                    <>
                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,80,80,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                            <X size={36} color="var(--neon-magenta)" />
                        </div>
                        <h1 className="headline" style={{ fontSize: '1.8rem', margin: '0 0 0.5rem' }}>
                            {userCancelled ? 'Plata anulată' : 'Plata a fost respinsă'}
                        </h1>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '1.2rem' }}>
                            {userCancelled
                                ? 'Nu s-a debitat nimic. Poți încerca din nou când vrei — abonamentul tău rămâne neschimbat.'
                                : 'Nu s-a debitat nimic. Verifică datele cardului, fondurile disponibile sau încearcă alt card — apoi reia plata.'}
                        </p>
                        <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => nav('/r/settings')} data-testid="result-retry">
                            Încearcă din nou
                        </button>
                    </>
                ) : (
                    <>
                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,200,80,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                            <RefreshCcw size={36} color="var(--amber)" className={!pollingExhausted && (loading || polls > 0) ? 'spin' : ''} />
                        </div>
                        <h1 className="headline" style={{ fontSize: '1.8rem', margin: '0 0 0.5rem' }}>Verificăm plata…</h1>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '1.2rem' }}>
                            {pollingExhausted
                                ? 'Confirmarea durează mai mult ca de obicei. Apasă „Verifică din nou" sau revino mai târziu din Setări → Abonament. Vei primi și un email când e procesată.'
                                : 'Procesatorul confirmă plata. Rămâi pe pagină câteva secunde…'}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {pollingExhausted && (
                                <button
                                    className="btn btn-primary"
                                    style={{ width: '100%' }}
                                    onClick={() => { setPayment((p) => p); setPolls(0); }}
                                    data-testid="result-recheck"
                                >
                                    <RefreshCcw size={14} /> Verifică din nou
                                </button>
                            )}
                            <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => nav('/r/settings')} data-testid="result-check-later">
                                Verifică mai târziu
                            </button>
                        </div>
                    </>
                )}
                <div style={{ marginTop: '1rem', fontSize: '0.78rem', color: 'var(--text-faint)' }}>
                    <Link to="/r" style={{ color: 'var(--text-faint)' }}>Înapoi la dashboard</Link>
                </div>
            </div>
        </div>
    );
}
